// utils/skillSystem.js
// Core ability and skill execution system for reuse across player and enemy AI

import { SOLDIER_CLASSES } from '../componets/classes';

/**
 * Initialize a unit with class data, unlocked skills, and ability tracking
 * @param {Object} unit - Base unit object with id, name, type, etc
 * @param {string} classId - ID of the soldier class
 * @param {Array<string>} unlockedSkillIds - Array of unlocked skill IDs
 * @returns {Object} Unit with class stats, abilities, and cooldown tracking
 */
export const initializeUnitWithClass = (unit, classId, unlockedSkillIds = []) => {
  const soldierClass = SOLDIER_CLASSES[classId];
  if (!soldierClass) {
    console.warn(`Class ${classId} not found, using base stats only`);
    return unit;
  }

  // Start with base class stats
  const stats = { ...soldierClass.baseStats };

  // Apply skill bonuses
  const skills = soldierClass.skills.filter(s => unlockedSkillIds.includes(s.id));
  skills.forEach(skill => {
    if (skill.effect) {
      Object.entries(skill.effect).forEach(([key, value]) => {
        if (typeof value === 'number' && stats[key] !== undefined) {
          stats[key] += value;
        }
      });
    }
  });

  // Initialize abilities with cooldown and usage tracking
  const abilities = soldierClass.abilities.map(ability => ({
    ...ability,
    currentCooldown: 0,
    usesRemaining: ability.usesPerMission || null, // null = unlimited
    originalUses: ability.usesPerMission
  }));

  return {
    ...unit,
    classId,
    unlockedSkillIds,
    ...stats,
    maxHp: stats.health,
    hp: stats.health,
    abilities,
    skills: skills.map(s => ({ id: s.id, name: s.name, effect: s.effect }))
  };
};

/**
 * Check if an ability can be used right now
 * @param {Object} unit - The unit attempting to use ability
 * @param {Object} ability - The ability object
 * @param {number} currentAP - Current action points of unit
 * @returns {Object} { canUse: boolean, reason: string }
 */
export const canUseAbility = (unit, ability, currentAP) => {
  // Check action points
  if (currentAP < (ability.apCost || 1)) {
    return { canUse: false, reason: 'insufficient_ap' };
  }

  // Check cooldown
  if (ability.currentCooldown > 0) {
    return { canUse: false, reason: 'cooldown', remaining: ability.currentCooldown };
  }

  // Check mission uses remaining
  if (ability.usesRemaining !== null && ability.usesRemaining <= 0) {
    return { canUse: false, reason: 'no_uses_remaining' };
  }

  return { canUse: true };
};

/**
 * Execute an ability and return effects to be applied
 * @param {Object} ability - The ability to execute
 * @param {Object} caster - Unit casting the ability
 * @param {Array<Object>} targets - Target unit(s)
 * @param {Object} helpers - Combat helpers { hasLineOfSight, getCoverBonus, getUnitAt }
 * @returns {Object} Effect object with results and messages
 */
export const executeAbility = (ability, caster, targets, helpers = {}) => {
  const messages = [];

  switch (ability.id) {
    // SUPPORT ABILITIES
    case 'medkit':
      return executeMedkit(caster, targets[0], messages);

    case 'revival_protocol':
      return executeRevival(caster, targets[0], messages);

    case 'smoke_bomb':
      return executeSmokeBomb(caster, targets, messages);

    case 'guardian':
      return executeGuardian(caster, targets[0], messages);

    // SNIPER ABILITIES
    case 'overwatch':
      return executeOverwatch(caster, messages);

    case 'deadeye':
      return executeDeadeye(caster, targets[0], messages);

    case 'disabling_shot':
      return executeDisablingShot(caster, targets[0], messages);

    case 'double_tap':
      return executeDoubleTap(caster, targets[0], messages);

    // RANGER ABILITIES
    case 'suppression':
      return executeSuppression(caster, targets[0], messages);

    case 'flashbang':
      return executeFlashbang(caster, targets, messages);

    case 'blade_storm':
      return executeBladestorm(caster, targets, messages);

    case 'serial_killer':
      return executeSerialKiller(caster, targets[0], messages);

    // HEAVY ABILITIES
    case 'suppress_fire':
      return executeSuppressingFire(caster, targets[0], messages);

    case 'holo_targeting':
      return executeHoloTargeting(caster, targets[0], messages);

    case 'rocket_launcher':
      return executeRocketLauncher(caster, targets, messages);

    case 'bunker_down':
      return executeBunkerDown(caster, messages);

    default:
      return { success: false, message: 'Unknown ability' };
  }
};

// ============ SUPPORT ABILITIES ============

const executeMedkit = (caster, target, messages) => {
  if (!target) {
    return { success: false, message: '⚠️ No target selected for Medkit' };
  }
  if (target.hp >= target.maxHp) {
    return { success: false, message: '⚠️ Target is already at full health' };
  }

  const healAmount = 40;
  const actualHeal = Math.min(healAmount, target.maxHp - target.hp);

  return {
    success: true,
    message: `💊 ${caster.name} used Medkit! ${target.name} healed for ${actualHeal} HP`,
    unitUpdates: [
      { unitId: target.id, updates: { hp: target.hp + actualHeal } }
    ],
    apCost: 1,
    usesUsed: 1
  };
};

const executeRevival = (caster, target, messages) => {
  if (!target || target.hp > 0) {
    return { success: false, message: '⚠️ Target must be downed to revive' };
  }

  const reviveHp = Math.ceil(target.maxHp * 0.6);

  return {
    success: true,
    message: `🔄 ${caster.name} revived ${target.name} at 60% HP!`,
    unitUpdates: [
      { unitId: target.id, updates: { hp: reviveHp } }
    ],
    apCost: 2,
    cooldown: 5,
    usesUsed: 1
  };
};

const executeSmokeBomb = (caster, targets, messages) => {
  const defenseBonus = 40;
  const duration = 2; // turns

  return {
    success: true,
    message: `💨 ${caster.name} deployed Smoke Bomb! Allies gain +${defenseBonus} defense`,
    areaEffect: {
      type: 'smoke',
      casterPosition: { x: caster.x, y: caster.y },
      radius: 3,
      defenseBonus,
      duration
    },
    apCost: 1,
    cooldown: 2
  };
};

const executeGuardian = (caster, target, messages) => {
  if (!target || target.type !== caster.type) {
    return { success: false, message: '⚠️ Can only protect allies' };
  }

  return {
    success: true,
    message: `🛡️ ${caster.name} is protecting ${target.name}! Redirecting 75% of damage for 2 turns`,
    unitUpdates: [
      { unitId: target.id, updates: { protectedBy: caster.id, protectionDuration: 2 } }
    ],
    apCost: 2,
    cooldown: 4,
    usesUsed: 1
  };
};

// ============ SNIPER ABILITIES ============

const executeOverwatch = (caster, messages) => {
  return {
    success: true,
    message: `🎯 ${caster.name} set Overwatch! Will react to enemy movement`,
    unitUpdates: [
      { unitId: caster.id, updates: { onOverwatch: true } }
    ],
    apCost: 0,
    cooldown: 0,
    endsPlayerTurn: true
  };
};

const executeDeadeye = (caster, target, messages) => {
  if (!target || target.type === caster.type) {
    return { success: false, message: '⚠️ Invalid target for Deadeye' };
  }

  const baseDamage = 25;
  const aimBonus = 30;

  const damage = baseDamage + (caster.aim || 0) / 5;

  return {
    success: true,
    message: `💥 ${caster.name} used Deadeye on ${target.name}! +${aimBonus} aim, guaranteed crit!`,
    unitUpdates: [
      { unitId: target.id, updates: { hp: Math.max(0, target.hp - damage) } }
    ],
    apCost: 1,
    cooldown: 4,
    usesUsed: 1,
    killMessage: target.hp <= damage ? `💀 ${target.name} eliminated!` : null
  };
};

const executeDisablingShot = (caster, target, messages) => {
  if (!target || target.type === caster.type) {
    return { success: false, message: '⚠️ Invalid target' };
  }

  const damage = 15;
  const aimPenalty = -30;
  const mobilityPenalty = -20;

  return {
    success: true,
    message: `🎯 ${caster.name} fired Disabling Shot at ${target.name}!`,
    unitUpdates: [
      {
        unitId: target.id,
        updates: {
          hp: Math.max(0, target.hp - damage),
          aim: (target.aim || 0) + aimPenalty,
          mobility: (target.mobility || 0) + mobilityPenalty
        }
      }
    ],
    apCost: 1,
    cooldown: 3
  };
};

const executeDoubleTap = (caster, target, messages) => {
  if (!target || target.type === caster.type) {
    return { success: false, message: '⚠️ Invalid target' };
  }

  const damagePerShot = 20;
  const totalDamage = damagePerShot * 2;

  return {
    success: true,
    message: `💥💥 ${caster.name} used Double Tap on ${target.name}! ${totalDamage} damage!`,
    unitUpdates: [
      { unitId: target.id, updates: { hp: Math.max(0, target.hp - totalDamage) } }
    ],
    apCost: 2,
    cooldown: 5,
    usesUsed: 1,
    killMessage: target.hp <= totalDamage ? `💀 ${target.name} eliminated!` : null
  };
};

// ============ RANGER ABILITIES ============

const executeSuppression = (caster, target, messages) => {
  if (!target || target.type === caster.type) {
    return { success: false, message: '⚠️ Invalid target' };
  }

  const aimPenalty = -30;

  return {
    success: true,
    message: `🔒 ${caster.name} suppressed ${target.name}! Aim reduced by 30`,
    unitUpdates: [
      {
        unitId: target.id,
        updates: {
          aim: (target.aim || 0) + aimPenalty,
          suppressed: true,
          suppressedDuration: 2
        }
      }
    ],
    apCost: 1,
    cooldown: 2
  };
};

const executeFlashbang = (caster, targets, messages) => {
  const aimPenalty = -25;
  const mobilityPenalty = -15;

  return {
    success: true,
    message: `💡 ${caster.name} detonated Flashbang! ${targets.length} enemies disoriented`,
    unitUpdates: targets.map(target => ({
      unitId: target.id,
      updates: {
        aim: (target.aim || 0) + aimPenalty,
        mobility: (target.mobility || 0) + mobilityPenalty
      }
    })),
    apCost: 1,
    cooldown: 3,
    usesUsed: 1
  };
};

const executeBladestorm = (caster, targets, messages) => {
  const damagePerTarget = 30;

  return {
    success: true,
    message: `⚔️ ${caster.name} used Blade Storm! Hit ${targets.length} enemies`,
    unitUpdates: targets.map(target => ({
      unitId: target.id,
      updates: { hp: Math.max(0, target.hp - damagePerTarget) }
    })),
    apCost: 2,
    cooldown: 4,
    usesUsed: 1
  };
};

const executeSerialKiller = (caster, targets, messages) => {
  return {
    success: true,
    message: `🔫 ${caster.name} activated Serial Killer! Unlimited shots until miss`,
    unitUpdates: [
      { unitId: caster.id, updates: { serialKillerActive: true, shotsMissed: 0 } }
    ],
    apCost: 0,
    cooldown: 5,
    usesUsed: 1,
    endsPlayerTurn: true
  };
};

// ============ HEAVY ABILITIES ============

const executeSuppressingFire = (caster, target, messages) => {
  if (!target || target.type === caster.type) {
    return { success: false, message: '⚠️ Invalid target' };
  }

  return {
    success: true,
    message: `🔫 ${caster.name} opened Suppressing Fire on ${target.name}!`,
    unitUpdates: [
      {
        unitId: target.id,
        updates: {
          suppressed: true,
          suppressedDuration: 2,
          aim: (target.aim || 0) - 30
        }
      }
    ],
    apCost: 1,
    cooldown: 1
  };
};

const executeHoloTargeting = (caster, target, messages) => {
  if (!target || target.type === caster.type) {
    return { success: false, message: '⚠️ Invalid target' };
  }

  return {
    success: true,
    message: `🎯 ${caster.name} marked ${target.name} with Holo-Targeting! Allies gain +20 aim`,
    unitUpdates: [
      { unitId: target.id, updates: { holoTargeted: true, duration: 3 } }
    ],
    apCost: 1,
    cooldown: 3
  };
};

const executeRocketLauncher = (caster, targets, messages) => {
  const baseDamage = 60;

  return {
    success: true,
    message: `🚀 ${caster.name} fired Rocket Launcher! ${targets.length} enemies hit`,
    unitUpdates: targets.map(target => ({
      unitId: target.id,
      updates: { hp: Math.max(0, target.hp - baseDamage) }
    })),
    apCost: 2,
    cooldown: 3,
    usesUsed: 1
  };
};

const executeBunkerDown = (caster, messages) => {
  const defenseBonus = 60;
  const damageBonus = 50;
  const duration = 3;

  return {
    success: true,
    message: `🛡️ ${caster.name} Bunked Down! +${defenseBonus} defense, +${damageBonus}% damage for ${duration} turns`,
    unitUpdates: [
      {
        unitId: caster.id,
        updates: {
          bunkeredDown: true,
          bunkeredDuration: duration,
          defenseBonus,
          damageBonus
        }
      }
    ],
    apCost: 1,
    cooldown: 4,
    usesUsed: 1,
    endsPlayerTurn: true
  };
};

// ============ STATE MANAGEMENT ============

/**
 * Apply ability effects to unit array and return updated units
 * @param {Object} effect - Effect object returned from executeAbility
 * @param {Array<Object>} units - Current units array
 * @returns {Array<Object>} Updated units
 */
export const applyAbilityEffect = (effect, units) => {
  if (!effect || !effect.unitUpdates) {
    return units;
  }

  return units.map(unit => {
    const update = effect.unitUpdates.find(u => u.unitId === unit.id);
    return update ? { ...unit, ...update.updates } : unit;
  });
};

/**
 * Decrement cooldowns and clear temporary effects
 * @param {Array<Object>} units - Current units
 * @returns {Array<Object>} Units with cooldowns decremented
 */
export const decrementAbilityCooldowns = (units) => {
  return units.map(unit => {
    if (!unit.abilities) return unit;

    const updatedAbilities = unit.abilities.map(ability => ({
      ...ability,
      currentCooldown: Math.max(0, ability.currentCooldown - 1)
    }));

    return { ...unit, abilities: updatedAbilities };
  });
};

/**
 * Set an ability on cooldown after use
 * @param {Object} unit - The unit that used ability
 * @param {Object} ability - The ability used
 * @param {number} cooldown - Cooldown turns
 * @returns {Object} Updated unit
 */
export const putAbilityOnCooldown = (unit, ability, cooldown) => {
  if (!unit.abilities) return unit;

  const updatedAbilities = unit.abilities.map(ab => {
    if (ab.id === ability.id) {
      return {
        ...ab,
        currentCooldown: cooldown,
        usesRemaining: ab.usesRemaining !== null ? ab.usesRemaining - 1 : null
      };
    }
    return ab;
  });

  return { ...unit, abilities: updatedAbilities };
};

/**
 * Get all available abilities for a unit right now
 * @param {Object} unit - The unit
 * @param {number} currentAP - Current AP
 * @returns {Array<Object>} Available abilities
 */
export const getAvailableAbilities = (unit, currentAP) => {
  if (!unit.abilities) return [];

  return unit.abilities.filter(ability => {
    const check = canUseAbility(unit, ability, currentAP);
    return check.canUse;
  });
};